import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

export type AppState = 'active' | 'background' | 'inactive';

interface UseAppStateReturn {
  appState: AppState;
  isActive: boolean;
  isBackground: boolean;
  isInactive: boolean;
}

/**
 * Hook to track app state changes (active, background, inactive)
 * Works on both native platforms (iOS/Android) and web
 */
export const useAppState = (): UseAppStateReturn => {
  const [appState, setAppState] = useState<AppState>('active');

  const handleAppStateChange = useCallback((state: { isActive: boolean }) => {
    if (state.isActive) {
      setAppState('active');
    } else {
      setAppState('background');
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      setAppState('background');
    } else {
      setAppState('active');
    }
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Native platform - use Capacitor App plugin
      let appStateListener: any = null;
      
      App.addListener('appStateChange', handleAppStateChange).then((listener) => {
        appStateListener = listener;
      });
      
      return () => {
        if (appStateListener) {
          appStateListener.remove();
        }
      };
    } else {
      // Web platform - use document visibility API
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Set initial state
      if (document.hidden) {
        setAppState('background');
      } else {
        setAppState('active');
      }
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [handleAppStateChange, handleVisibilityChange]);

  return {
    appState,
    isActive: appState === 'active',
    isBackground: appState === 'background',
    isInactive: appState === 'inactive',
  };
};

/**
 * Hook to detect when app comes from background to foreground
 * Returns a callback that fires when the app becomes active from background
 */
export const useAppForeground = (callback: () => void) => {
  const { appState } = useAppState();
  const [wasBackground, setWasBackground] = useState(false);

  useEffect(() => {
    if (appState === 'background') {
      setWasBackground(true);
    } else if (appState === 'active' && wasBackground) {
      // App came from background to foreground
      callback();
      setWasBackground(false);
    }
  }, [appState, wasBackground, callback]);
};
